import { useState } from 'react';
import { Building2, DollarSign } from 'lucide-react';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { Empresa, PLANOS, formatUsd, InatividadeRow } from './types';

interface Props {
  empresas: Empresa[];
  custoMedioPorEmpresa: number;
  custoMedioPorPlano: { plano: string; label: string; empresas: number; media: number }[];
  inatividade?: Record<string, InatividadeRow>;
  onOpenEmpresa: (empresaId: string) => void;
  onOpenCost: (empresaId: string, nome: string) => void;
}


export const EmpresasSummaryCard = ({
  empresas,
  custoMedioPorEmpresa,
  custoMedioPorPlano,
  inatividade = {},
  onOpenEmpresa,
  onOpenCost,
}: Props) => {
  const [open, setOpen] = useState(false);

  return (
    <>
      <Card
        className="cursor-pointer hover:shadow-lg transition-shadow"
        onClick={() => setOpen(true)}
      >
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Building2 className="w-5 h-5" />
            Empresas
          </CardTitle>
          <CardDescription>Clique para ver a lista</CardDescription>
        </CardHeader>
        <CardContent>
          <p className="text-2xl font-bold">{empresas.length}</p>
          <p className="text-sm text-muted-foreground">empresas ativas</p>
          <p className="text-xs text-muted-foreground mt-2">
            Custo médio por empresa: {formatUsd(custoMedioPorEmpresa)}
          </p>
          <div className="mt-1 space-y-0.5">
            {custoMedioPorPlano.map((p) => (
              <p key={p.plano} className="text-xs text-muted-foreground">
                {p.label}: {formatUsd(p.media)} / empresa ({p.empresas})
              </p>
            ))}
          </div>
        </CardContent>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Building2 className="w-5 h-5" />
              Empresas Ativas ({empresas.length})
            </DialogTitle>
          </DialogHeader>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Nome</TableHead>
                <TableHead>Plano</TableHead>
                <TableHead>Produto</TableHead>
                <TableHead>Feedbacks</TableHead>
                <TableHead>Sem uso</TableHead>
                <TableHead>Lembrete</TableHead>
                <TableHead>Cadastro</TableHead>
                <TableHead className="w-[60px]">Custos</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {empresas.map((empresa) => (
                <TableRow
                  key={empresa.id}
                  className="cursor-pointer hover:bg-muted/50"
                  onClick={() => {
                    setOpen(false);
                    onOpenEmpresa(empresa.id);
                  }}
                >
                  <TableCell className="font-medium">{empresa.nome}</TableCell>
                  <TableCell>
                    {PLANOS.find((p) => p.value === empresa.plano)?.label || empresa.plano}
                  </TableCell>
                  <TableCell>{empresa.produto || '-'}</TableCell>
                  <TableCell>{empresa.feedbacks}</TableCell>
                  <TableCell>
                    {(() => {
                      const dias = inatividade[empresa.id]?.dias ?? 0;
                      const cor =
                        dias >= 21
                          ? 'text-red-600'
                          : dias >= 7
                            ? 'text-amber-600'
                            : 'text-green-600';
                      return <span className={`font-medium ${cor}`}>{dias} d</span>;
                    })()}
                  </TableCell>
                  <TableCell>
                    {(() => {
                      const row = inatividade[empresa.id];
                      if (!row?.nivel || !row?.lembreteEm) {
                        return <span className="text-muted-foreground">Não enviado</span>;
                      }
                      return (
                        <span className="text-green-600 font-medium">
                          {row.nivel}º · {new Date(row.lembreteEm).toLocaleDateString('pt-BR')}
                        </span>
                      );
                    })()}
                  </TableCell>
                  <TableCell>
                    {empresa.created_at
                      ? new Date(empresa.created_at).toLocaleDateString('pt-BR')
                      : '-'}
                  </TableCell>
                  <TableCell>

                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={(e) => {
                        e.stopPropagation();
                        onOpenCost(empresa.id, empresa.nome);
                      }}
                      title="Ver custos"
                    >
                      <DollarSign className="h-4 w-4 text-green-600" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
              {empresas.length === 0 && (
                <TableRow>
                  <TableCell colSpan={8} className="text-center py-10 text-muted-foreground">
                    Nenhuma empresa cadastrada.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </DialogContent>
      </Dialog>
    </>
  );
};
